/**
 * Stable cross-session visitor id + tab-scoped anonymous session for analytics joins
 * (Firestore anonymousActions + server-side albumViews / event views / shared collection opens).
 */

const VISITOR_STORAGE_KEY = 'experio_analytics_visitor_id';
const ANON_SESSION_KEY = 'experio_analytics_anon_session_id';

function randomId(): string {
   if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
   }
   return `v_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

/** Persists in localStorage for joining client + server events across sessions. */
export function getOrCreateVisitorId(): string {
   if (typeof window === 'undefined' || !window.localStorage) {
      return randomId();
   }
   try {
      let id = window.localStorage.getItem(VISITOR_STORAGE_KEY);
      if (!id || id.length < 8) {
         id = randomId();
         window.localStorage.setItem(VISITOR_STORAGE_KEY, id);
      }
      return id;
   } catch {
      return randomId();
   }
}

/** One id per browser tab session (sessionStorage). */
export function getOrCreateAnonSessionId(): string {
   if (typeof window === 'undefined' || !window.sessionStorage) {
      return `anon_sess_${randomId()}`;
   }
   try {
      let id = window.sessionStorage.getItem(ANON_SESSION_KEY);
      if (!id || id.length < 8) {
         id = `anon_sess_${randomId()}`;
         window.sessionStorage.setItem(ANON_SESSION_KEY, id);
      }
      return id;
   } catch {
      return `anon_sess_${randomId()}`;
   }
}

/** UTM + common marketing params from current URL (safe for analytics payloads). */
export function captureUtmSnapshot(): Record<string, string> {
   if (typeof window === 'undefined') return {};
   try {
      const p = new URLSearchParams(window.location.search);
      const keys = [
         'utm_source',
         'utm_medium',
         'utm_campaign',
         'utm_term',
         'utm_content',
         'gclid',
         'fbclid',
         'src',
      ];
      const out: Record<string, string> = {};
      for (const k of keys) {
         const v = p.get(k);
         if (v && v.trim()) out[k] = v.trim().slice(0, 512);
      }
      return out;
   } catch {
      return {};
   }
}

export function getAnalyticsAppVersion(): string {
   return process.env.REACT_APP_VERSION || '1.0.0';
}

/** Optional fields for server-side public view APIs (join with Firestore userActions / anonymousActions). */
export function getPublicViewAnalyticsPayload(): {
   visitorId: string;
   anonSessionId: string;
   utmSnapshot: Record<string, string>;
   appVersion: string;
} {
   return {
      visitorId: getOrCreateVisitorId(),
      anonSessionId: getOrCreateAnonSessionId(),
      utmSnapshot: captureUtmSnapshot(),
      appVersion: getAnalyticsAppVersion(),
   };
}
